import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import PredictionTab from "../../components/PredictionTab";
import authFetch from "../../utils/authFetch";
import {
  capitalizeFirstLetter,
  calculateAge,
  calculateLocalDOB,
} from "../../shared";
import { usePredictionSocket } from "../../shared/hooks/usePredictionSocket";
import PredictionPage from "../../components/PredictionPage";
import Loading from "../../components/loading/Loading";

const UserPredictionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userError, setUserError] = useState("");
  // Refs to prevent duplicate API calls
  const hasFetchedUser = useRef(false);
  const lastFetchedId = useRef(null);
  
  // Initialize socket connection with the user ID
  const { connect, disconnect, connected, connectionStatus } = usePredictionSocket(id);

  // Socket connection effect
  useEffect(() => {
    if (id) {
      connect();
    }
    // Cleanup function - disconnect when component unmounts or user changes
    return () => {
      disconnect();
    };
  }, [id, connect, disconnect]);

  useEffect(() => {
    if (!id) return;

    // First check if user data was passed through navigation state
    const passedUserData = location.state?.userData;
    
    if (passedUserData && passedUserData._id === id) {
      // Use the passed user data directly - no API call needed
      setCurrentUser(passedUserData);
      setLoadingUser(false);
      setUserError("");
      hasFetchedUser.current = true;
      lastFetchedId.current = id;
      return;
    }

    // If no user data was passed, fall back to API call (for direct URL access)
    let isCancelled = false;

    const fetchUser = async () => {
      // ✅ Prevent refetch if we already have cached user
      if (
        lastFetchedId.current === id &&
        hasFetchedUser.current &&
        currentUser
      ) {
        return;
      }

      try {
        if (!isCancelled) {
          setLoadingUser(true);
          setUserError("");
        }

        let allUsers = [];
        let page = 1;
        let hasMorePages = true;

        while (hasMorePages && !isCancelled) {
          const response = await authFetch.get(
            `${process.env.REACT_APP_BACKEND_URL}/user/admin/sandbox-customer?page=${page}&limit=100`
          );

          if (isCancelled) return;

          if (response.data.success && response.data.data.sandboxCustomers) {
            allUsers = [...allUsers, ...response.data.data.sandboxCustomers];

            const foundUser = allUsers.find((user) => user._id === id);
            if (foundUser && !isCancelled) {
              setCurrentUser(foundUser);
              hasFetchedUser.current = true;
              lastFetchedId.current = id;
              setLoadingUser(false);
              return;
            }

            const totalPages = response.data.data.meta?.totalPages || 1;
            hasMorePages = page < totalPages;
            page++;
          } else {
            hasMorePages = false;
          }
        }

        if (!isCancelled) {
          setUserError("User not found");
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching user:", error);
          setUserError(error.response?.data?.message || "Failed to fetch user");
        }
      } finally {
        if (!isCancelled) {                
          setLoadingUser(false);
        }
      }
    };

    // ✅ Reset cache if ID changes
    if (lastFetchedId.current !== id) {
      hasFetchedUser.current = false;
      setCurrentUser(null);
    }

    fetchUser();

    return () => {
      isCancelled = true;
    };
  }, [id, location.state]); // Added location.state to dependencies

  const handleBackClick = () => {
    // Disconnect socket before navigating away
    disconnect();
    navigate("/prediction");
  };

  if (loadingUser) {
    return (
      <Loading text=" Loading user data..."/>
    );
  }

  if (userError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {userError}
        </Alert>
        <Button
          onClick={handleBackClick}
          variant="outlined"
          sx={{ minWidth: "auto", px: 1 }}
        >
          <ArrowBack />
        </Button>
      </Box>
    );
  }  

  if (!currentUser) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          User not found
        </Alert>
        <Button
          onClick={handleBackClick}
          variant="outlined"
          sx={{ minWidth: "auto", px: 1 }}
        >
          <ArrowBack />
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          onClick={handleBackClick}
          variant="outlined"
          sx={{ minWidth: "auto", px: 1 }}
        >
          <ArrowBack />
        </Button>
        <Typography variant="h4" sx={{ fontWeight: "bold" }} component="span">
          {capitalizeFirstLetter(
            currentUser?.name || currentUser?.email || "Unknown User"
          )}
        </Typography>
        <Typography variant="h4" component="span" sx={{ fontWeight: "normal" }}>
          {(() => {
            const age = calculateAge(currentUser?.birthDetails?.date) || 0;
            const runningAge = age + 1;
            const dob =
              calculateLocalDOB(
                currentUser?.birthDetails?.date,
                currentUser?.birthDetails?.utcOffset
              ) || "?";
            const timezone = currentUser?.userCurrentTimezone || "N/A";
            return ` (${dob}, ${runningAge} years, ${timezone})`;
          })()}
        </Typography>
      </Box>
      {/* <PredictionTab userId={id} currentUser={currentUser} /> */}
      <PredictionPage userId={id} currentUser={currentUser} />
    </Box>
  );
};

export default UserPredictionPage;
